import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useLazyGetResourcesQuery } from '../../state/apis/generated/resourcesApi'
import type { ITreeSelectOption } from '../../components/shared/TreeSelectInput'

interface UseResourcesTreeDataProps {
  namespace?: string
  resourceType?: string
  skip?: boolean
}

interface UseResourcesTreeDataResult {
  treeData: ITreeSelectOption[]
  isHierarchy: boolean
  isLoading: boolean
  loadChildren: (node: ITreeSelectOption) => Promise<void>
  reset: () => void
}

export const useResourcesTreeData = ({
  namespace,
  resourceType,
  skip = false
}: UseResourcesTreeDataProps): UseResourcesTreeDataResult => {
  const [treeData, setTreeData] = useState<ITreeSelectOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isHierarchy, setIsHierarchy] = useState(false)
  const loadedParentsRef = useRef<Set<string>>(new Set())
  const prevParamsRef = useRef<{ namespace?: string; resourceType?: string }>({})

  const [fetchResources] = useLazyGetResourcesQuery()

  const fullResourceType = namespace && resourceType ? `${namespace}/${resourceType}` : undefined

  const reset = useCallback(() => {
    setTreeData([])
    setIsHierarchy(false)
    // eslint-disable-next-line functional/immutable-data
    loadedParentsRef.current = new Set()
  }, [])

  // Clear data when namespace or resourceType changes
  useEffect(() => {
    const paramsChanged =
      prevParamsRef.current.namespace !== namespace ||
      prevParamsRef.current.resourceType !== resourceType

    if (paramsChanged) {
      reset()
      // eslint-disable-next-line functional/immutable-data
      prevParamsRef.current = { namespace, resourceType }
    }
  }, [namespace, resourceType, reset])

  // Load initial data when resourceType changes
  useEffect(() => {
    const loadInitialData = async () => {
      if (skip || !namespace || !resourceType) {
        return
      }

      setIsLoading(true)

      try {
        const result = await fetchResources({
          namespace,
          resourceType: fullResourceType
        }).unwrap()

        if (result?.resources) {
          const initialData = result.resources.map((resource) => ({
            key: resource.resourceId || '',
            title: resource.displayName || resource.resourceId || '',
            isLeaf: !resource.hasChildren
          }))
          setTreeData(initialData)
          setIsHierarchy(result.isHierarchy)
        }
      } catch (error) {
        console.error('Failed to load initial resources:', error)
        setTreeData([])
      } finally {
        setIsLoading(false)
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadInitialData()
  }, [namespace, resourceType, fullResourceType, skip, fetchResources])

  const updateTreeData = useCallback(
    (
      list: ITreeSelectOption[],
      key: string,
      children: ITreeSelectOption[]
    ): ITreeSelectOption[] => {
      return list.map((node) => {
        if (node.key === key) {
          return { ...node, children }
        }
        if (node.children) {
          return { ...node, children: updateTreeData(node.children, key, children) }
        }
        return node
      })
    },
    []
  )

  const loadChildren = useCallback(
    async (node: ITreeSelectOption): Promise<void> => {
      if (!namespace || !resourceType) {
        return
      }

      // Skip if already loaded or is a leaf node
      if (node.isLeaf || loadedParentsRef.current.has(node.key)) {
        return
      }

      try {
        const result = await fetchResources({
          namespace,
          resourceType: fullResourceType,
          parentId: node.key
        }).unwrap()

        if (result?.resources) {
          const children: ITreeSelectOption[] = result.resources.map((resource) => ({
            key: resource.resourceId || '',
            title: resource.displayName || resource.resourceId || '',
            isLeaf: !resource.hasChildren
          }))
          setTreeData((origin) => updateTreeData(origin, node.key, children))
          loadedParentsRef.current.add(node.key)
        }
      } catch (error) {
        console.error('Failed to load children:', error)
      }
    },
    [namespace, resourceType, fullResourceType, fetchResources, updateTreeData]
  )

  return useMemo(
    () => ({
      treeData,
      isHierarchy,
      isLoading,
      loadChildren,
      reset
    }),
    [treeData, isHierarchy, isLoading, loadChildren, reset]
  )
}
